import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import axios from 'axios';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { GetAdProductMetricsResponse, GetAdProductMetricsResponseRecord } from './types';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { calculatePercentChange } from 'src/utils/app';
import { topicTrafficServerProxy, getEntityIdToNameMap, GetEntityMetadataProductRecord } from '../../serverProxy';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { SlPaginator } from 'src/components/BeaconV2Components/SlPaginator';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

type ProductMap = Map<string, { name: string; record: GetEntityMetadataProductRecord }>;

const ITEMS_PER_PAGE = 20;
/**
 * Shows products for the topics paid search page
 */
export function PaidSearchProductsGrid() {
  const [page, setPage] = useState(1);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useRetailerId();
  const baseRequest = useBaseTopicRequestBuilder();

  const adProductMetricsRequest = baseRequest
    .setRetailerId(+retailerId)
    .setSortField('adClicks')
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .build();

  const adProductMetricsComparisonRequest = new TopicRequestBuilder(adProductMetricsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      'PaidSearchTopicsProductsGrid',
      adProductMetricsRequest,
      adProductMetricsComparisonRequest
    ],
    queryFn: async () => {
      const endpoint = '/api/beacontopics/GetAdProductMetrics';
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetAdProductMetricsResponse>(endpoint, adProductMetricsRequest),
        axios.post<GetAdProductMetricsResponse>(endpoint, adProductMetricsComparisonRequest)
      ]);

      return [response, comparisonResponse];
    }
  });
  const [mainResponse, comparisonResponse] = data ?? [{ records: [] }, { records: [] }];
  const stacklineSkus = mainResponse.records
    .map((record) => record.stacklineSku)
    .slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // Get the product metadata by page, otherwise it will be too slow if
  // we fetch them all at once
  const { data: products, isLoading: metadataLoading } = useQuery({
    queryKey: ['PaidSearchTopicsProductsGridMetadata', stacklineSkus],
    queryFn: async () => {
      const productsInner = await topicTrafficServerProxy.getEntityMetadata({
        entityIds: stacklineSkus,
        entityType: 'product',
        retailerId: +retailerId
      });
      return getEntityIdToNameMap(productsInner);
    },
    enabled: !isLoading
  });

  const productsMap: ProductMap = products ?? new Map();

  const comparisonStacklineSkuToRecord = comparisonResponse.records.reduce((acc, record) => {
    acc.set(record.stacklineSku, record);
    return acc;
  }, new Map<string, GetAdProductMetricsResponseRecord>());

  const columnDefs = [
    {
      headerName: 'Products',
      valueFormatter: (_, row: GetAdProductMetricsResponseRecord) => {
        const product = productsMap.get(row.stacklineSku);
        return <ProductCell stacklineSku={row.stacklineSku} title={product?.name} />;
      },
      tableColumnProps: {
        width: 300
      }
    },
    {
      headerName: 'Campaigns',
      field: 'campaigns',
      valueFormatter: (value: number, row: GetAdProductMetricsResponseRecord) => {
        const comparisonValue = comparisonStacklineSkuToRecord.get(row.stacklineSku)?.campaigns ?? 0;
        const delta = value - comparisonValue;

        return (
          <MetricCell
            metricType={METRICTYPE.VOLUME}
            value={value}
            comparisonNode={
              <Text color={delta >= 0 ? 'success' : 'error'}>{`(${delta >= 0 ? '+' : ''}${delta})`}</Text>
            }
          />
        );
      }
    },
    ...(
      [
        ['adClicks', 'Ad Clicks', METRICTYPE.VOLUME],
        ['adSpend', 'Ad Spend', METRICTYPE.MONEY],
        ['adSales', 'Ad Sales', METRICTYPE.MONEY],
        ['adUnitsSold', 'Ad Units Sold', METRICTYPE.VOLUME],
        ['roas', 'ROAS', METRICTYPE.MONEY]
      ] as const
    ).map(([field, title, metricType]) => {
      return {
        headerName: title,
        field,
        valueFormatter: (value, row: GetAdProductMetricsResponseRecord) => {
          const comparisonValue = comparisonStacklineSkuToRecord.get(row.stacklineSku)?.[field] ?? 0;

          return (
            <MetricCell
              metricType={metricType}
              value={value}
              percentChange={calculatePercentChange(value, comparisonValue)}
            />
          );
        }
      };
    })
  ];

  return (
    <SlPaginator
      onPageChange={setPage}
      items={mainResponse.records}
      itemsPerPage={ITEMS_PER_PAGE}
      loading={isLoading || metadataLoading}
      renderLoader={() => (
        <EntityTable
          skeletonRows={ITEMS_PER_PAGE}
          isLoading
          rowData={[]}
          columnDefs={columnDefs}
          showPagination={false}
        />
      )}
      renderItems={(items) => (
        <EntityTable
          isLoading={isLoading || metadataLoading}
          shouldModifyColumnDefs={false}
          rowData={items}
          showPagination={false}
          columnDefs={columnDefs}
        />
      )}
    />
  );
}
